import { useState, useMemo } from "react";
import { TrendingDown, TrendingUp, Sparkles } from "lucide-react";
import { fmtEurInt } from "../../lib/format";

const heading = { fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif" };

export const CommissionCalculator = () => {
  const [revenue, setRevenue] = useState(50000);
  const [marketplaceRate, setMarketplaceRate] = useState(20);

  const data = useMemo(() => {
    const marketplaceCost = (revenue * marketplaceRate) / 100;
    const nautiqoCost = (revenue * 3) / 100;
    const savings = marketplaceCost - nautiqoCost;
    const savingsPct = revenue > 0 ? (savings / revenue) * 100 : 0;
    return { marketplaceCost, nautiqoCost, savings, savingsPct };
  }, [revenue, marketplaceRate]);

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 sm:p-8 shadow-2xl shadow-blue-900/30" data-testid="commission-calculator">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-blue-300" />
        </div>
        <span className="text-blue-300 text-xs font-bold uppercase tracking-wider">Calcola il tuo risparmio</span>
      </div>

      <div className="space-y-5">
        {/* Revenue slider */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-sm text-white/70 font-medium">Fatturato annuo del tuo charter</label>
            <span className="text-2xl font-bold text-white tabular-nums" style={heading} data-testid="calc-revenue">
              {fmtEurInt(revenue)}
            </span>
          </div>
          <input
            type="range"
            min="10000"
            max="500000"
            step="5000"
            value={revenue}
            onChange={(e) => setRevenue(Number(e.target.value))}
            className="w-full nautiqo-slider"
            data-testid="calc-revenue-slider"
            aria-label="Fatturato annuo"
          />
          <div className="flex justify-between text-[11px] text-white/35 mt-1">
            <span>€10k</span><span>€100k</span><span>€500k</span>
          </div>
        </div>

        {/* Marketplace rate slider */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-sm text-white/70 font-medium">Commissione marketplace attuale</label>
            <span className="text-2xl font-bold text-white tabular-nums" style={heading} data-testid="calc-rate">
              {marketplaceRate}%
            </span>
          </div>
          <input
            type="range"
            min="10"
            max="30"
            step="1"
            value={marketplaceRate}
            onChange={(e) => setMarketplaceRate(Number(e.target.value))}
            className="w-full nautiqo-slider nautiqo-slider-amber"
            data-testid="calc-rate-slider"
            aria-label="Commissione marketplace"
          />
          <div className="flex justify-between text-[11px] text-white/35 mt-1">
            <span>10%</span><span>20%</span><span>30%</span>
          </div>
        </div>

        {/* Comparison */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4" data-testid="calc-marketplace-cost">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-300 mb-2">
              <TrendingDown className="w-3.5 h-3.5" /> Marketplace {marketplaceRate}%
            </div>
            <div className="text-xl sm:text-2xl font-bold text-red-200 tabular-nums" style={heading}>
              {fmtEurInt(data.marketplaceCost)}
            </div>
            <p className="text-[11px] text-red-300/70 mt-1">commissioni/anno</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4" data-testid="calc-nautiqo-cost">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-300 mb-2">
              <TrendingUp className="w-3.5 h-3.5" /> Nautiqo 3%
            </div>
            <div className="text-xl sm:text-2xl font-bold text-emerald-200 tabular-nums" style={heading}>
              {fmtEurInt(data.nautiqoCost)}
            </div>
            <p className="text-[11px] text-emerald-300/70 mt-1">commissioni/anno</p>
          </div>
        </div>

        {/* Savings highlight */}
        <div className="bg-gradient-to-br from-blue-500/15 via-blue-400/10 to-emerald-500/10 border border-blue-400/25 rounded-2xl p-5 relative overflow-hidden" data-testid="calc-savings">
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-blue-400/20 rounded-full blur-3xl" />
          <div className="relative">
            <p className="text-xs text-blue-200/80 font-semibold uppercase tracking-wider mb-1">Risparmio annuale</p>
            <p className="text-3xl sm:text-4xl font-bold text-white tabular-nums" style={heading}>
              {fmtEurInt(data.savings)}
            </p>
            <p className="text-sm text-blue-100/70 mt-1">
              Il <span className="font-semibold text-white">{(100 - data.savingsPct).toFixed(0)}%</span> del tuo fatturato resta a te.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
